import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import {
  CheckboxSelector,
  CustomFieldsEditableTable,
  Label,
  Notification,
  Notice,
} from 'shared/components';
import {
  platformFeatures,
  defaultDisabledFeatureMessage,
} from 'shared/constants';
import {
  isFeatureEnabled,
  isFeatureAvailable,
  mapCustomFieldsToSelector,
} from 'shared/helpers';
import './styles.scss';
import { useTranslation } from 'react-i18next';

const CustomFieldsTab = ({
  selectedProduct,
  selectedPolicy,
  handleCustomFieldsSelect,
  selectedCustomFields,
  setCustomFieldsTabError,
  handleFieldsEdit,
}) => {
  const { t } = useTranslation();
  const customFields = get(selectedProduct, 'data.custom_fields', []);
  const customFieldOptions = mapCustomFieldsToSelector(selectedProduct.data);

  // Transform selectedCustomFields to match dropdown format (use product_custom_field IDs)
  const transformedSelectedFields = selectedCustomFields.map((field) => ({
    ...field,
    value: get(field, 'product_custom_field'),
    label: get(field, 'name'),
  }));

  // Enrich selectedCustomFields with product data for the table
  const enrichedCustomFields = selectedCustomFields.map((policyField) => {
    const productField = customFields.find(
      (pf) => pf.id === get(policyField, 'product_custom_field'),
    );
    return {
      ...policyField,
      value: get(policyField, 'product_custom_field'), // Use product field ID for matching
      data: productField || {},
      name: get(productField, 'name', get(policyField, 'name')),
      label: get(productField, 'name', get(policyField, 'name')),
    };
  });

  const isCustomFieldsFeatureEnabled = isFeatureEnabled(
    platformFeatures.extra_custom_fields,
  );
  const notAvailableMessage = isCustomFieldsFeatureEnabled
    ? t('Your plan allows for one custom field.')
    : t('Custom Fields are not available in your plan.');

  const handleSelection = (val) => {
    if (!isFeatureEnabled(platformFeatures.extra_custom_fields)) {
      Notification('error', t(defaultDisabledFeatureMessage));
      return false;
    }
    // Transform dropdown values back to policy field structure
    const transformedValues = val.map((dropdownField) => {
      // Find the corresponding policy field by product_custom_field ID
      const existingPolicyField = selectedCustomFields.find(
        (policyField) =>
          get(policyField, 'product_custom_field') === dropdownField.value,
      );

      if (existingPolicyField) {
        return existingPolicyField;
      }

      // Create new policy field structure for newly selected item
      return {
        id: null, // Will be set by backend when saved
        data_type: null,
        default_value: get(
          dropdownField,
          'cf_value',
          get(dropdownField, 'data.default_value', ''),
        ),
        description: null,
        license_template: get(selectedPolicy, 'id'),
        name: get(dropdownField, 'label', get(dropdownField, 'data.name')),
        product_custom_field: dropdownField.value,
        label: get(dropdownField, 'label', get(dropdownField, 'data.name')),
        value: dropdownField.value,
      };
    });

    return handleCustomFieldsSelect(transformedValues);
  };

  return (
    <section className="CustomFieldsTab product-features-section">
      <div className="CustomFields-field">
        <div className="CustomFields-selector">
          <Label text={t('Select custom fields')} />
          <CheckboxSelector
            text={t('Custom fields list')}
            options={customFieldOptions}
            value={transformedSelectedFields}
            onChangeCallback={handleSelection}
            onMenuClose={() => {}}
            featureAvailable={isFeatureAvailable(
              platformFeatures.extra_custom_fields,
            )}
            notAvailableMessage={notAvailableMessage}
          />
        </div>
        <Notice theme="warning" title={t('License custom fields')}>
          {t(
            'Setting a value here will override the default value set in the Product custom fields configuration',
          )}
        </Notice>
        <div>
          <CustomFieldsEditableTable
            selectedCustomFields={enrichedCustomFields}
            setErrorState={setCustomFieldsTabError}
            handleFieldsEdit={handleFieldsEdit}
            productCustomFields={customFields}
          />
        </div>
      </div>
    </section>
  );
};

CustomFieldsTab.propTypes = {
  selectedProduct: PropTypes.object.isRequired,
  selectedPolicy: PropTypes.object,
  handleCustomFieldsSelect: PropTypes.func.isRequired,
  selectedCustomFields: PropTypes.array.isRequired,
  setCustomFieldsTabError: PropTypes.func.isRequired,
  handleFieldsEdit: PropTypes.func.isRequired,
};

export default CustomFieldsTab;
